/*
 * HomeFit room planner — Commune Lifestyle
 * Custom element <home-fit>. Reads its config from the JSON script inside the
 * element (rendered by sections/homefit.liquid) and builds the planner UI.
 *   mode "product": the product being viewed is placed in the room.
 *   mode "page":    standalone planner (templates/page.homefit.json).
 * Product search uses /search?view=homefit (templates/search.homefit.liquid).
 * No dependencies. Units: cm internally, metres in the UI.
 */
(function () {
  if (customElements.get('home-fit')) return;

  /* ---------- Space each type of piece needs to be used comfortably (cm) ---------- */
  var TYPE_RULES = {
    'sofas':              { front: 80 },
    'accent chairs':      { front: 60 },
    'chairs':             { front: 45 },
    'benches':            { front: 45 },
    'ottomans':           { low: true },
    'coffee tables':      { low: true, front: 45, back: 40 },
    'side & end tables':  { low: true },
    'nightstands':        { low: true },
    'stools & barstools': { low: true },
    'tv units':           { front: 60 },
    'console tables':     { front: 60 },
    'sideboards':         { front: 70 },
    'bar cabinets':       { front: 70 },
    'cabinets':           { front: 70 },
    'drawer chests':      { front: 70 },
    'dressers':           { front: 90 },
    'shelvings':          { front: 60 },
    'dining tables':      { front: 75, back: 75, sides: 60 },
    'dining sets':        { front: 75, back: 75, sides: 60 },
    'diniing sets':       { front: 75, back: 75, sides: 60 },
    'desks':              { front: 90 },
    'beds':               { front: 70, sides: 60 },
    'bed':                { front: 70, sides: 60 },
    'mattresses':         { front: 70, sides: 60 },
    'bar carts':          { front: 50 },
    'trolleys':           { front: 50 },
    'changing carts':     { front: 50 },
    'dividers':           {},
    'mirrors':            {}
  };
  var DEFAULT_PRESETS = [
    { label: 'HDB 4-room living', width: 6.6, depth: 3.6, doorWall: 'left', doorFrom: 2.5, doorWidth: 0.9 },
    { label: 'HDB master bedroom', width: 3.6, depth: 3.3, doorWall: 'bottom', doorFrom: 0.1, doorWidth: 0.85 },
    { label: 'Condo living', width: 5.0, depth: 3.2, doorWall: 'bottom', doorFrom: 3.9, doorWidth: 0.9 }
  ];
  var STORE_KEY = 'commune-homefit-v1';
  var SVGNS = 'http://www.w3.org/2000/svg';
  var E = 0.5;
  var RANK = { ok: 0, warn: 1, bad: 2 };

  /* ---------- helpers ---------- */
  function m(cm) { return (cm / 100).toFixed(2) + ' m'; }
  function snap(v, s) { s = s || 5; return Math.round(v / s) * s; }
  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : null; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function short(s, n) { s = String(s || ''); return s.length > n ? s.slice(0, n - 1).trim() + '…' : s; }

  /** Parse "L170 X W92 X H80", "W 180 x D 90 x H 75", "Ø120 X H75", "170 x 92 x 80" → {w, d, round} in cm. */
  function parseDims(str) {
    if (!str) return null;
    var s = String(str).toUpperCase().replace(/CM/g, ' ');
    // Cushions, covers and other flat add-ons aren't floor furniture
    var hm = s.match(/(?:^|[^A-Z])H\s*:?\s*([\d.]+)/);
    if (hm && +hm[1] > 1 && +hm[1] < 25) return null;
    var r = s.match(/(?:Ø|⌀|DIA\.?|DIAMETER)\s*:?\s*([\d.]+)/);
    if (r && +r[1] >= 20) return { w: +r[1], d: +r[1], round: true };
    var pairs = [], re = /(?:^|[^A-Z])([LWD])\s*:?\s*([\d.]+)/g, x;
    while ((x = re.exec(s))) pairs.push(+x[2]);
    if (pairs.length >= 2 && pairs[0] >= 20 && pairs[1] >= 20) return { w: pairs[0], d: pairs[1] };
    var nums = s.match(/\d+(?:\.\d+)?/g);
    if (nums && nums.length >= 2 && +nums[0] >= 20 && +nums[1] >= 20) return { w: +nums[0], d: +nums[1] };
    return null;
  }

  /** Footprint + clearance rules for one variant of a product (P = homefit-product-json). */
  function specFor(P, v) {
    var w = num(v && v.width != null ? v.width : P.width), d = num(v && v.depth != null ? v.depth : P.depth), round = !!P.round;
    if (!(w >= 20 && d >= 20)) {
      var p = parseDims(v && v.dims);
      if (!p) return null;
      w = p.w; d = p.d; round = round || !!p.round;
    }
    var rule = TYPE_RULES[String(P.type || '').trim().toLowerCase()] || {};
    var sides = num(P.clearSides) != null ? num(P.clearSides) : (rule.sides || 0);
    return {
      w: Math.round(w), d: Math.round(d), round: round,
      low: P.low != null ? !!P.low : !!rule.low,
      clear: {
        front: num(P.clearFront) != null ? num(P.clearFront) : (rule.front || 0),
        back: num(P.clearBack) != null ? num(P.clearBack) : (rule.back || 0),
        left: sides, right: sides
      }
    };
  }
  /** Distinct sizes a product comes in (one entry per footprint), available variants first. */
  function sizesOf(P) {
    if (!P || P.enabled === false) return [];
    var seen = {}, out = [];
    (P.variants || []).slice().sort(function (a, b) { return (b.available ? 1 : 0) - (a.available ? 1 : 0); }).forEach(function (v) {
      var spec = specFor(P, v); if (!spec) return;
      var k = spec.w + 'x' + spec.d;
      if (seen[k]) return; seen[k] = true;
      out.push({ variantId: v.id, title: v.title, price: v.price, available: v.available, spec: spec });
    });
    return out.sort(function (a, b) { return a.spec.w * a.spec.d - b.spec.w * b.spec.d; });
  }

  function fp(it) { var even = it.rot % 180 === 0; return { x: it.x, y: it.y, w: even ? it.w : it.d, h: even ? it.d : it.w }; }
  function dirOf(side, rot) {
    var v = { front: [0, 1], back: [0, -1], right: [1, 0], left: [-1, 0] }[side];
    for (var k = 0; k < (rot / 90) % 4; k++) v = [-v[1], v[0]];
    return v;
  }
  function zoneRect(f, v, c) {
    if (v[1] === 1) return { x: f.x, y: f.y + f.h, w: f.w, h: c };
    if (v[1] === -1) return { x: f.x, y: f.y - c, w: f.w, h: c };
    if (v[0] === 1) return { x: f.x + f.w, y: f.y, w: c, h: f.h };
    return { x: f.x - c, y: f.y, w: c, h: f.h };
  }
  function ovX(a, b) { return b.x < a.x + a.w - E && b.x + b.w > a.x + E; }
  function ovY(a, b) { return b.y < a.y + a.h - E && b.y + b.h > a.y + E; }
  function hit(a, b) { return ovX(a, b) && ovY(a, b); }
  function clip(z, R) {
    var x1 = Math.max(0, z.x), y1 = Math.max(0, z.y), x2 = Math.min(R.w, z.x + z.w), y2 = Math.min(R.d, z.y + z.h);
    return (x2 - x1 > 0.5 && y2 - y1 > 0.5) ? { x: x1, y: y1, w: x2 - x1, h: y2 - y1 } : null;
  }
  function el(tag, attrs, parent, text) {
    var n = document.createElementNS(SVGNS, tag);
    for (var k in attrs) n.setAttribute(k, attrs[k]);
    if (text != null) n.textContent = text;
    if (parent) parent.appendChild(n);
    return n;
  }
  function store(get, val) {
    try {
      if (get) return JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      localStorage.setItem(STORE_KEY, JSON.stringify(val));
    } catch (e) { return null; }
  }
  function track(action, extra) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('homefit_' + action, extra || {});
    } catch (e) { /* analytics is optional */ }
  }

  /* ======================================================================= */
  class HomeFit extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      try { this.cfg = JSON.parse(this.querySelector('[data-hf-config]').textContent); } catch (e) { return; }
      this.app = this.querySelector('[data-hf-app]');
      this.isProduct = this.cfg.mode === 'product' && !!this.cfg.current;
      this.uid = 0;
      this.presets = (this.cfg.presets && this.cfg.presets.length) ? this.cfg.presets : DEFAULT_PRESETS;
      this.cards = {};          // card key -> product JSON (suggested + search results)
      this.suggested = (this.cfg.companions || []).filter(P => sizesOf(P).length);

      var spec = null;
      if (this.isProduct) {
        this.curSizes = sizesOf(this.cfg.current);
        if (!this.curSizes.length) return; // nothing measurable: stay hidden
        spec = this.currentSpec(this.cfg.selectedVariantId);
      }

      this.state = { room: null, door: null, items: [], sel: null };
      this.restore(spec);
      this.build();
      this.hidden = false;
      this.render();

      if (this.isProduct) {
        this._onVariant = (e) => { var v = e.detail && e.detail.variant; if (v) this.setCurrentVariant(v.id); };
        document.addEventListener('variant:change', this._onVariant);
      }
      track('viewed', { mode: this.cfg.mode, product: this.isProduct ? this.cfg.current.productId : null });
    }
    disconnectedCallback() { if (this._onVariant) document.removeEventListener('variant:change', this._onVariant); }

    /* ---------- data ---------- */
    currentSpec(variantId) {
      var P = this.cfg.current;
      var v = (P.variants || []).find(x => x.id === variantId) || P.variants[0];
      return specFor(P, v) || (this.curSizes[0] && this.curSizes[0].spec) || null;
    }
    makeItem(o) {
      return Object.assign({ id: ++this.uid, x: 0, y: 0, rot: 0 }, o, { clear: Object.assign({ front: 0, back: 0, left: 0, right: 0 }, o.clear) });
    }
    productItem(P, size) {
      return this.makeItem(Object.assign({ kind: 'product', key: 'p' + P.productId, productId: P.productId, variantId: size.variantId, url: P.url, name: P.title, ptype: P.type, size: size.title }, size.spec));
    }
    current() { return this.state.items.find(i => i.kind === 'current'); }

    restore(spec) {
      var saved = this.cfg.remember ? store(true) : null;
      var S = this.state, d = this.cfg.defaults || {}, curSaved = null;
      if (saved && saved.room && saved.room.w >= 150) {
        S.room = saved.room; S.door = saved.door || { wall: 'none', at: 0, width: 90 };
        (saved.items || []).forEach(p => {
          if (p.kind === 'companion') p.kind = 'product'; // v1 saves
          if (p.kind !== 'product' && p.kind !== 'custom') return;
          if (this.isProduct && p.productId === this.cfg.current.productId) { curSaved = p; return; }
          S.items.push(this.makeItem(p));
        });
      } else {
        S.room = { w: Math.round((num(d.roomWidth) || 5) * 100), d: Math.round((num(d.roomDepth) || 3.2) * 100) };
        S.door = { wall: 'bottom', at: Math.max(0, S.room.w - 110), width: 90 };
      }
      this.fixDoor();
      if (spec) {
        var cur = this.makeItem(Object.assign({ kind: 'current', productId: this.cfg.current.productId, name: this.cfg.current.title, ptype: this.cfg.current.type }, spec));
        var pos = curSaved || (saved && saved.currentPos);
        if (pos) { cur.x = pos.x; cur.y = pos.y; cur.rot = pos.rot || 0; this.clampIn(cur); if (this.collides(cur)) this.place(cur); }
        else { var f = fp(cur); cur.x = snap((S.room.w - f.w) / 2); cur.y = 0; this.clampIn(cur); if (this.collides(cur)) this.place(cur); }
        S.items.unshift(cur);
        S.sel = cur.id;
      }
      S.items.forEach(it => this.clampIn(it));
    }
    serialise(includeCurrent) {
      var cur = this.current();
      return {
        room: this.state.room, door: this.state.door,
        currentPos: cur ? { x: cur.x, y: cur.y, rot: cur.rot } : null,
        items: this.state.items.filter(i => includeCurrent || i.kind !== 'current').map(i => ({
          kind: i.kind === 'current' ? 'product' : i.kind, key: i.key || ('p' + i.productId), productId: i.productId,
          variantId: i.kind === 'current' ? this.selectedVariantId() : i.variantId,
          url: i.kind === 'current' ? this.cfg.current.url : i.url, size: i.size,
          name: i.name, ptype: i.ptype, w: i.w, d: i.d, round: i.round, low: i.low, clear: i.clear, x: i.x, y: i.y, rot: i.rot
        }))
      };
    }
    save() { if (this.cfg.remember) store(false, this.serialise(false)); }
    selectedVariantId() { return this._variantId || this.cfg.selectedVariantId; }
    setCurrentVariant(id) {
      this._variantId = id;
      var P = this.cfg.current, v = (P.variants || []).find(x => x.id === id);
      var spec = v ? specFor(P, v) : null, cur = this.current();
      if (!spec) { if (cur) this.state.items = this.state.items.filter(i => i !== cur); this.variantMissing = true; this.render(); return; }
      this.variantMissing = false;
      if (cur) { Object.assign(cur, spec, { clear: Object.assign({ front: 0, back: 0, left: 0, right: 0 }, spec.clear) }); this.clampIn(cur); }
      else { cur = this.makeItem(Object.assign({ kind: 'current', productId: P.productId, name: P.title, ptype: P.type }, spec)); this.place(cur); this.state.items.unshift(cur); }
      this.render();
    }

    /* ---------- geometry on state ---------- */
    doorZone() {
      var D = this.state.door, R = this.state.room;
      if (!D || D.wall === 'none') return null;
      if (D.wall === 'top') return { x: D.at, y: 0, w: D.width, h: D.width };
      if (D.wall === 'bottom') return { x: D.at, y: R.d - D.width, w: D.width, h: D.width };
      if (D.wall === 'left') return { x: 0, y: D.at, w: D.width, h: D.width };
      return { x: R.w - D.width, y: D.at, w: D.width, h: D.width };
    }
    fixDoor() {
      var D = this.state.door, R = this.state.room;
      var len = (D.wall === 'top' || D.wall === 'bottom') ? R.w : R.d;
      D.width = Math.min(D.width, len); D.at = Math.max(0, Math.min(D.at, len - D.width));
    }
    clampIn(it) {
      var f = fp(it), R = this.state.room;
      it.x = Math.max(0, Math.min(it.x, R.w - f.w)); it.y = Math.max(0, Math.min(it.y, R.d - f.h));
      if (f.w > R.w) it.x = 0; if (f.h > R.d) it.y = 0;
    }
    collides(it) {
      var f = fp(it), dz = this.doorZone();
      if (dz && hit(f, dz)) return true;
      return this.state.items.some(o => o !== it && hit(f, fp(o)));
    }
    /** Find the free spot that leaves every piece the most comfortable (fewest clashes / tight gaps). */
    place(it) {
      var R = this.state.room, items = this.state.items, added = items.indexOf(it) === -1;
      if (added) items.push(it);
      var best = null, step = Math.max(10, snap(Math.max(R.w, R.d) / 40, 10));
      var rots = it.round ? [0] : [0, 90, 180, 270];
      outer:
      for (var ri = 0; ri < rots.length; ri++) {
        it.rot = rots[ri];
        var f0 = fp(it), xs = [], ys = [], v;
        if (f0.w > R.w || f0.h > R.d) continue;
        for (v = 0; v < R.w - f0.w; v += step) xs.push(v); xs.push(R.w - f0.w);
        for (v = 0; v < R.d - f0.h; v += step) ys.push(v); ys.push(R.d - f0.h);
        for (var yi = 0; yi < ys.length; yi++) for (var xi = 0; xi < xs.length; xi++) {
          it.x = xs[xi]; it.y = ys[yi]; var f = fp(it);
          if (this.collides(it)) continue;
          var score = 0;
          this.evaluate().forEach(r => { score += r.status === 'bad' ? 100 : r.status === 'warn' ? 10 : 0; });
          var bv = dirOf('back', it.rot);
          var atWall = (bv[1] === -1 && f.y === 0) || (bv[1] === 1 && f.y + f.h === R.d) || (bv[0] === -1 && f.x === 0) || (bv[0] === 1 && f.x + f.w === R.w);
          if (!atWall && !it.low && !it.clear.back) score += 1;
          if (!best || score < best.score) best = { score: score, x: it.x, y: it.y, rot: it.rot };
          if (score === 0) break outer;
        }
      }
      if (added) items.splice(items.indexOf(it), 1);
      if (best) { it.x = best.x; it.y = best.y; it.rot = best.rot; return; }
      it.rot = 0; var g = fp(it); it.x = snap((R.w - g.w) / 2); it.y = snap((R.d - g.h) / 2); this.clampIn(it);
    }
    gap(f, v, obstacles) {
      var R = this.state.room, g;
      if (v[1] === 1) { g = R.d - (f.y + f.h); obstacles.forEach(o => { if (ovX(f, o) && o.y + o.h > f.y + f.h - E) g = Math.min(g, Math.max(0, o.y - (f.y + f.h))); }); }
      else if (v[1] === -1) { g = f.y; obstacles.forEach(o => { if (ovX(f, o) && o.y < f.y + E) g = Math.min(g, Math.max(0, f.y - (o.y + o.h))); }); }
      else if (v[0] === 1) { g = R.w - (f.x + f.w); obstacles.forEach(o => { if (ovY(f, o) && o.x + o.w > f.x + f.w - E) g = Math.min(g, Math.max(0, o.x - (f.x + f.w))); }); }
      else { g = f.x; obstacles.forEach(o => { if (ovY(f, o) && o.x < f.x + E) g = Math.min(g, Math.max(0, f.x - (o.x + o.w))); }); }
      return Math.max(0, g);
    }
    evaluate() {
      var R = this.state.room, dz = this.doorZone(), items = this.state.items;
      var fps = new Map(items.map(it => [it.id, fp(it)]));
      return items.map(it => {
        var f = fps.get(it.id), issues = [], status = 'ok', zones = [];
        var bump = (s, msg) => { if (RANK[s] > RANK[status]) status = s; issues.push(msg); };
        if (f.x < -E || f.y < -E || f.x + f.w > R.w + E || f.y + f.h > R.d + E) bump('bad', 'Too big for this room');
        var over = items.find(o => o.id !== it.id && hit(f, fps.get(o.id)));
        if (over) bump('bad', 'Overlaps ' + short(over.name, 28));
        if (dz && hit(f, dz)) bump('bad', 'Blocks the door');
        var obstacles = items.filter(o => o.id !== it.id && !o.low).map(o => fps.get(o.id));
        ['front', 'back', 'left', 'right'].forEach(side => {
          var need = it.clear[side]; if (!need) return;
          var v = dirOf(side, it.rot), g = this.gap(f, v, obstacles);
          var s = g >= need - 0.5 ? 'ok' : g >= need * 0.6 ? 'warn' : 'bad';
          zones.push({ side: side, v: v, need: need, g: g, s: s, z: zoneRect(f, v, need) });
          if (s !== 'ok') bump(s, (side === 'front' ? 'In front' : side === 'back' ? 'Behind' : 'On the ' + side) + ': ' + m(g) + ', needs ' + m(need));
        });
        var front = zones.find(z => z.side === 'front');
        var summary = issues.length ? issues.join(' · ')
          : front ? 'In front: ' + m(front.g) + ' clear (needs ' + m(front.need) + ')'
          : it.low ? 'Low piece, easy to walk around' : 'Fits with room to spare';
        return { it: it, f: f, status: status, zones: zones, summary: summary };
      });
    }

    /* ---------- UI shell ---------- */
    build() {
      var sid = this.dataset.sectionId;
      var presetBtns = this.presets.map((p, i) =>
        `<button type="button" class="hf-chip" data-preset="${i}" aria-pressed="false">${esc(p.label)} · ${(+p.width).toFixed(1)} × ${(+p.depth).toFixed(1)} m</button>`).join('');
      var typeBtns = (this.cfg.searchTypes || []).filter(Boolean).map(t =>
        `<button type="button" class="hf-chip" data-type="${esc(t)}" aria-pressed="false">${esc(t)}</button>`).join('');
      var fullLink = this.isProduct && this.cfg.plannerUrl
        ? `<a class="hf-fulllink" href="${esc(this.cfg.plannerUrl)}" data-hf="full">Open the full room planner →</a>` : '';
      this.app.innerHTML = `
        <div class="hf-grid">
          <div class="hf-col">
            <div class="hf-sub"><span class="hf-label">Start from a typical room (approx.)</span><div class="hf-chips" data-hf="presets">${presetBtns}</div></div>
            <div class="hf-fields">
              <div class="hf-f"><label for="hf-rw-${sid}">Room width</label><div class="hf-inp"><input id="hf-rw-${sid}" data-hf="rw" type="number" step="0.05" min="1.5" max="15" inputmode="decimal"><span>m</span></div></div>
              <div class="hf-f"><label for="hf-rd-${sid}">Room depth</label><div class="hf-inp"><input id="hf-rd-${sid}" data-hf="rd" type="number" step="0.05" min="1.5" max="15" inputmode="decimal"><span>m</span></div></div>
            </div>
            <div class="hf-sub">
              <span class="hf-label">Door</span>
              <div class="hf-fields hf-fields--3">
                <div class="hf-f"><label for="hf-dw-${sid}">Wall</label><div class="hf-inp"><select id="hf-dw-${sid}" data-hf="dw"><option value="top">Top</option><option value="right">Right</option><option value="bottom">Bottom</option><option value="left">Left</option><option value="none">No door</option></select></div></div>
                <div class="hf-f"><label for="hf-dp-${sid}">From corner</label><div class="hf-inp"><input id="hf-dp-${sid}" data-hf="dp" type="number" step="0.05" min="0" inputmode="decimal"><span>m</span></div></div>
                <div class="hf-f"><label for="hf-dx-${sid}">Width</label><div class="hf-inp"><input id="hf-dx-${sid}" data-hf="dx" type="number" step="0.05" min="0.6" max="2" inputmode="decimal"><span>m</span></div></div>
              </div>
            </div>
            <div class="hf-verdict" data-hf="verdict" role="status" aria-live="polite"></div>
            <dl class="hf-stats" data-hf="stats"></dl>
            <div class="hf-sub"><span class="hf-label">In your room</span><ul class="hf-pieces" data-hf="pieces"></ul></div>
            ${fullLink}
          </div>
          <div class="hf-col hf-plancol">
            <div class="hf-planbox">
              <div class="hf-planhead"><span class="hf-label">Your room, from above</span><span class="hf-hint">Drag to move · tap to select · R rotates · arrows nudge</span></div>
              <svg class="hf-plan" data-hf="plan" role="application" aria-label="Top-down room plan"></svg>
              <div class="hf-toolbar" data-hf="toolbar"></div>
              <div class="hf-legend">
                <span><i class="hf-sw"></i>Furniture, seen from above (sofas show back, arms and cushions)</span>
                <span><i class="hf-sw hf-sw--zone"></i>Space needed to use it</span>
                <span><i class="hf-sw hf-sw--door"></i>Door swing</span>
              </div>
            </div>
          </div>
        </div>
        <div class="hf-add">
          <div class="hf-addhead"><h3 class="h4">Add to your room</h3>
            ${this.cfg.showCartButton ? '<button type="button" class="btn btn--small" data-hf="cart" hidden></button>' : ''}</div>
          ${this.cfg.showSearch !== false ? `
          <form class="hf-search" data-hf="search" role="search">
            <div class="hf-inp hf-inp--search"><label class="visually-hidden" for="hf-q-${sid}">Search products</label>
              <input id="hf-q-${sid}" name="q" type="search" autocomplete="off" placeholder="Search Commune furniture, e.g. Cove, coffee table, bed frame">
              <button type="submit" class="hf-btn">Search</button></div>
            ${typeBtns ? `<div class="hf-chips" data-hf="types">${typeBtns}</div>` : ''}
          </form>
          <p class="hf-status" data-hf="status" aria-live="polite"></p>` : ''}
          <div class="hf-cards" data-hf="cards"></div>
          <button type="button" class="btn btn--secondary btn--small hf-more" data-hf="more" hidden>Show more</button>
          <form class="hf-own" data-hf="own">
            <span class="hf-label">Add something you already own</span>
            <div class="hf-fields hf-fields--own">
              <div class="hf-f"><label for="hf-on-${sid}">Name</label><div class="hf-inp"><input id="hf-on-${sid}" name="name" type="text" maxlength="30" placeholder="e.g. Dining table"></div></div>
              <div class="hf-f"><label for="hf-ow-${sid}">Width</label><div class="hf-inp"><input id="hf-ow-${sid}" name="w" type="number" min="20" max="600" inputmode="numeric" required><span>cm</span></div></div>
              <div class="hf-f"><label for="hf-od-${sid}">Depth</label><div class="hf-inp"><input id="hf-od-${sid}" name="d" type="number" min="20" max="600" inputmode="numeric" required><span>cm</span></div></div>
              <button type="submit" class="btn btn--secondary btn--small">Add</button>
            </div>
          </form>
        </div>`;
      this.$ = (k) => this.app.querySelector('[data-hf="' + k + '"]');
      this.svg = this.$('plan');
      this.showSuggested();
      this.syncInputs();
      this.bind();
    }

    /* ---------- product cards (suggested + search results) ---------- */
    showSuggested() {
      this.results = { label: this.isProduct ? 'Suggested with this piece' : 'Suggested pieces', list: this.suggested, more: false };
      this.renderCards();
      var st = this.$('status');
      if (st) st.textContent = this.suggested.length ? '' : (this.isProduct ? '' : 'Search above or pick a category to start adding furniture.');
    }
    renderCards(append) {
      var box = this.$('cards'), R = this.results;
      var html = R.list.map((P, i) => {
        var key = (R.tag || 's') + (R.offset || 0) + '_' + i, sizes = sizesOf(P);
        this.cards[key] = P;
        var inPlan = this.state.items.some(it => it.productId === P.productId);
        var img = P.image ? `<img src="${esc(P.image)}" alt="" loading="lazy" width="180" height="120">` : '<div class="hf-card__ph"></div>';
        if (!sizes.length) {
          return `<div class="hf-card is-nosize">${img}<a class="hf-card__title" href="${esc(P.url)}">${esc(P.title)}</a>
            <span class="hf-card__meta">Size not listed online</span><a class="hf-btn" href="${esc(P.url)}">View product</a></div>`;
        }
        var sel = sizes.length > 1
          ? `<div class="hf-inp hf-inp--sm"><label class="visually-hidden" for="hf-sz-${key}">Size</label><select id="hf-sz-${key}" data-size="${key}">${sizes.map((s, j) =>
              `<option value="${j}">${s.spec.w} × ${s.spec.d} cm${s.title && s.title !== 'Default Title' ? ' · ' + esc(short(s.title, 26)) : ''}</option>`).join('')}</select></div>`
          : `<span class="hf-card__meta">${sizes[0].spec.w} × ${sizes[0].spec.d} cm</span>`;
        return `<div class="hf-card">${img}
          <a class="hf-card__title" href="${esc(P.url)}">${esc(P.title)}</a>
          <span class="hf-card__price">${esc(P.price)}</span>
          ${sel}
          <button type="button" class="btn btn--small ${inPlan ? 'btn--secondary' : ''}" data-card="${key}">${inPlan ? 'Add another' : '+ Add to room'}</button>
        </div>`;
      }).join('');
      if (append) box.insertAdjacentHTML('beforeend', html); else box.innerHTML = html;
      box.hidden = !box.innerHTML.trim();
      var more = this.$('more'); more.hidden = !R.more;
    }
    refreshCardButtons() {
      this.app.querySelectorAll('[data-card]').forEach(b => {
        var P = this.cards[b.dataset.card], inPlan = this.state.items.some(it => it.productId === P.productId);
        b.textContent = inPlan ? 'Add another' : '+ Add to room'; b.classList.toggle('btn--secondary', inPlan);
      });
    }
    search(q, label, page) {
      var st = this.$('status'); if (!q) return;
      if (this._abort) this._abort.abort();
      this._abort = window.AbortController ? new AbortController() : null;
      page = page || 1;
      st.textContent = 'Searching…';
      var url = (this.cfg.searchUrl || '/search') + '?view=homefit&type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(q) + '&page=' + page;
      fetch(url, { signal: this._abort ? this._abort.signal : undefined, credentials: 'same-origin' })
        .then(r => r.text())
        .then(t => {
          var data = JSON.parse(t);
          // only pieces with a usable floor size (skips cushions, covers, accessories)
          var list = (data.products || []).filter(P => P.enabled !== false && sizesOf(P).length);
          this.results = { list: list, more: !!data.hasMore, tag: 'q', offset: page * 100, q: q, label: label, page: page };
          this.renderCards(page > 1);
          var shown = this.$('cards').querySelectorAll('.hf-card').length;
          st.textContent = shown ? (label || '“' + q + '”') + ': showing ' + shown + ' piece' + (shown === 1 ? '' : 's')
            : (data.hasMore ? 'No measurable pieces on this page. Try “Show more” or another word.' : 'No furniture found for ' + (label || '“' + q + '”') + '. Try another word.');
          if (page === 1) track('searched', { q: q });
        })
        .catch(err => { if (err && err.name === 'AbortError') return; st.textContent = 'Search is unavailable right now. Please try again.'; });
    }

    syncInputs() {
      var S = this.state, q = (k) => this.$(k);
      q('rw').value = (S.room.w / 100).toFixed(2); q('rd').value = (S.room.d / 100).toFixed(2);
      q('dw').value = S.door.wall; q('dp').value = (S.door.at / 100).toFixed(2); q('dx').value = (S.door.width / 100).toFixed(2);
      q('dp').disabled = q('dx').disabled = S.door.wall === 'none';
      this.syncPresets();
    }
    syncPresets() {
      var S = this.state;
      this.app.querySelectorAll('[data-preset]').forEach(b => {
        var p = this.presets[+b.dataset.preset];
        b.setAttribute('aria-pressed', Math.round(p.width * 100) === S.room.w && Math.round(p.depth * 100) === S.room.d);
      });
    }

    bind() {
      var S = this.state, read = (k, lo, hi, fb) => { var v = num(this.$(k).value); return v != null ? Math.round(Math.max(lo, Math.min(hi, v)) * 100) : fb; };
      ['rw', 'rd'].forEach(k => {
        this.$(k).addEventListener('input', () => {
          S.room.w = read('rw', 1.5, 15, S.room.w); S.room.d = read('rd', 1.5, 15, S.room.d);
          this.fixDoor(); this.render(); this.syncPresets();
          if (!this._trackedRoom) { this._trackedRoom = true; track('room_entered', { mode: this.cfg.mode }); }
        });
        this.$(k).addEventListener('change', () => this.syncInputs());
      });
      this.$('dw').addEventListener('change', () => { S.door.wall = this.$('dw').value; this.fixDoor(); this.syncInputs(); this.render(); });
      ['dp', 'dx'].forEach(k => {
        this.$(k).addEventListener('input', () => { S.door.at = read('dp', 0, 15, S.door.at); S.door.width = read('dx', 0.6, 2, S.door.width); this.fixDoor(); this.render(); });
        this.$(k).addEventListener('change', () => this.syncInputs());
      });
      this.$('presets').addEventListener('click', (e) => {
        var b = e.target.closest('[data-preset]'); if (!b) return;
        var p = this.presets[+b.dataset.preset];
        S.room = { w: Math.round(p.width * 100), d: Math.round(p.depth * 100) };
        S.door = { wall: p.doorWall || 'none', at: Math.round((p.doorFrom || 0) * 100), width: Math.round((p.doorWidth || 0.9) * 100) };
        this.fixDoor(); S.items.forEach(it => this.clampIn(it)); this.syncInputs(); this.render();
      });

      /* search */
      var form = this.$('search');
      if (form) {
        var timer, input = form.q;
        var clearTypes = () => { var t = this.$('types'); if (t) t.querySelectorAll('[data-type]').forEach(b => b.setAttribute('aria-pressed', 'false')); };
        form.addEventListener('submit', (e) => { e.preventDefault(); clearTypes(); var q = input.value.trim(); if (q) this.search(q); });
        input.addEventListener('input', () => {
          clearTimeout(timer); clearTypes();
          var q = input.value.trim();
          if (q.length < 2) { if (!q) this.showSuggested(); return; }
          timer = setTimeout(() => this.search(q), 350);
        });
        var types = this.$('types');
        if (types) types.addEventListener('click', (e) => {
          var b = e.target.closest('[data-type]'); if (!b) return;
          var on = b.getAttribute('aria-pressed') === 'true';
          clearTypes(); input.value = '';
          if (on) { this.showSuggested(); return; }
          b.setAttribute('aria-pressed', 'true');
          this.search('product_type:"' + b.dataset.type + '"', b.dataset.type);
        });
      }
      this.$('more').addEventListener('click', () => { var R = this.results; if (R && R.q) this.search(R.q, R.label, (R.page || 1) + 1); });
      this.$('cards').addEventListener('click', (e) => {
        var b = e.target.closest('[data-card]'); if (!b) return;
        var key = b.dataset.card, P = this.cards[key], sizes = sizesOf(P);
        var selEl = this.app.querySelector('[data-size="' + key + '"]');
        var size = sizes[selEl ? +selEl.value : 0]; if (!size) return;
        var it = this.productItem(P, size);
        this.place(it); S.items.push(it); S.sel = it.id; this.render(); this.refreshCardButtons();
        track('piece_added', { product: P.productId });
        if (window.matchMedia('(max-width: 900px)').matches) this.svg.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      this.$('own').addEventListener('submit', (e) => {
        e.preventDefault();
        var f = e.target, w = Math.round(num(f.w.value)), d = Math.round(num(f.d.value));
        if (!(w >= 20 && d >= 20)) return;
        var it = this.makeItem({ kind: 'custom', name: (f.name.value || 'My furniture').trim(), w: Math.min(w, 600), d: Math.min(d, 600), clear: {} });
        this.place(it); S.items.push(it); S.sel = it.id; f.reset(); this.render();
      });
      this.$('pieces').addEventListener('click', (e) => { var li = e.target.closest('li[data-id]'); if (!li) return; S.sel = +li.dataset.id; this.render(); });
      this.$('toolbar').addEventListener('click', (e) => this.onTool(e));
      if (this.$('cart')) this.$('cart').addEventListener('click', () => this.addToCart());
      if (this.$('full')) this.$('full').addEventListener('click', () => {
        // carry this product into the full planner
        if (this.cfg.remember) store(false, this.serialise(true));
        track('open_full_planner', { product: this.cfg.current.productId });
      });

      /* drag */
      var svg = this.svg, drag = null;
      var pt = (e) => { var p = svg.createSVGPoint(); p.x = e.clientX; p.y = e.clientY; return p.matrixTransform(svg.getScreenCTM().inverse()); };
      svg.addEventListener('pointerdown', (e) => {
        var g = e.target.closest('.hf-piece');
        if (!g) { S.sel = null; this.render(); return; }
        var it = S.items.find(i => i.id === +g.dataset.id); S.sel = it.id;
        var p = pt(e); drag = { it: it, dx: p.x - it.x, dy: p.y - it.y };
        svg.setPointerCapture(e.pointerId); svg.classList.add('is-dragging'); this.render(); e.preventDefault();
      });
      svg.addEventListener('pointermove', (e) => {
        if (!drag) return;
        var p = pt(e), it = drag.it, R = S.room, SN = 12;
        it.x = snap(p.x - drag.dx); it.y = snap(p.y - drag.dy);
        var f = fp(it);
        if (Math.abs(it.x) < SN) it.x = 0; if (Math.abs(it.y) < SN) it.y = 0;
        if (Math.abs(R.w - (it.x + f.w)) < SN) it.x = R.w - f.w; if (Math.abs(R.d - (it.y + f.h)) < SN) it.y = R.d - f.h;
        this.clampIn(it); this.renderPlan(this.evaluate());
      });
      var end = () => { if (!drag) return; drag = null; svg.classList.remove('is-dragging'); this.render(true); };
      svg.addEventListener('pointerup', end); svg.addEventListener('pointercancel', end);
      svg.addEventListener('keydown', (e) => {
        var g = e.target.closest('.hf-piece'); if (!g) return;
        var it = S.items.find(i => i.id === +g.dataset.id); S.sel = it.id;
        var st = e.shiftKey ? 25 : 5;
        var mv = { ArrowLeft: [-st, 0], ArrowRight: [st, 0], ArrowUp: [0, -st], ArrowDown: [0, st] }[e.key];
        if (mv) { it.x += mv[0]; it.y += mv[1]; this.clampIn(it); }
        else if (e.key === 'r' || e.key === 'R') this.rotate(it);
        else if ((e.key === 'Delete' || e.key === 'Backspace') && it.kind !== 'current') { this.remove(it); return; }
        else return;
        e.preventDefault(); this.render(true);
      });
    }

    /* ---------- actions ---------- */
    rotate(it) {
      var f = fp(it), cx = f.x + f.w / 2, cy = f.y + f.h / 2;
      it.rot = (it.rot + 90) % 360; var n = fp(it);
      it.x = snap(cx - n.w / 2); it.y = snap(cy - n.h / 2); this.clampIn(it);
    }
    toWall(it) {
      var f = fp(it), R = this.state.room, cx = f.x + f.w / 2, cy = f.y + f.h / 2;
      var d = { top: cy, bottom: R.d - cy, left: cx, right: R.w - cx };
      var wall = Object.keys(d).sort((a, b) => d[a] - d[b])[0];
      it.rot = { top: 0, bottom: 180, left: 270, right: 90 }[wall];
      var n = fp(it); it.x = snap(cx - n.w / 2); it.y = snap(cy - n.h / 2);
      if (wall === 'top') it.y = 0; if (wall === 'bottom') it.y = R.d - n.h; if (wall === 'left') it.x = 0; if (wall === 'right') it.x = R.w - n.w;
      this.clampIn(it);
    }
    remove(it) { this.state.items = this.state.items.filter(i => i !== it); this.state.sel = null; this.render(); this.refreshCardButtons(); }
    onTool(e) {
      var b = e.target.closest('button'); if (!b) return;
      var S = this.state, it = S.items.find(i => i.id === S.sel), act = b.dataset.act;
      if (act === 'clear') { S.items = S.items.filter(i => i.kind === 'current'); S.sel = null; this.render(); this.refreshCardButtons(); return; }
      if (!it) return;
      if (act === 'rot') this.rotate(it);
      if (act === 'wall') this.toWall(it);
      if (act === 'centre') { var f = fp(it); it.x = snap((S.room.w - f.w) / 2); it.y = snap((S.room.d - f.h) / 2); }
      if (act === 'del') { this.remove(it); return; }
      this.render();
    }
    cartItems() {
      var counts = {};
      this.state.items.filter(i => i.kind === 'product' && i.variantId).forEach(i => { counts[i.variantId] = (counts[i.variantId] || 0) + 1; });
      return Object.keys(counts).map(id => ({ id: +id, quantity: counts[id] }));
    }
    addToCart() {
      var items = this.cartItems(); if (!items.length) return;
      var btn = this.$('cart'); btn.disabled = true;
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(r => r.json().then(j => ({ ok: r.ok, j: j })))
        .then(res => {
          btn.disabled = false;
          if (!res.ok) { btn.textContent = res.j.description || 'Some pieces could not be added'; return; }
          document.dispatchEvent(new CustomEvent('cart:build'));
          document.dispatchEvent(new CustomEvent('cart:open'));
          track('added_to_cart', { mode: this.cfg.mode, items: items.length });
        })
        .catch(() => { btn.disabled = false; btn.textContent = 'Could not reach the cart. Try again.'; });
    }

    /* ---------- render ---------- */
    render(keepFocus) {
      var results = this.evaluate();
      this.renderPlan(results); this.renderSide(results); this.renderToolbar(); this.renderCartBtn();
      this.save();
      if (keepFocus && this.state.sel != null) {
        var n = this.svg.querySelector('[data-id="' + this.state.sel + '"]'); if (n) n.focus({ preventScroll: true });
      }
    }
    renderCartBtn() {
      var b = this.$('cart'); if (!b) return;
      var n = this.cartItems().reduce((a, i) => a + i.quantity, 0);
      b.hidden = !n;
      b.textContent = 'Add ' + n + (this.isProduct ? ' other' : '') + ' planned piece' + (n > 1 ? 's' : '') + ' to cart';
    }
    renderPlan(results) {
      var S = this.state, R = S.room, svg = this.svg, pad = 46, fs = Math.max(10, Math.min(16, Math.max(R.w, R.d) / 42));
      svg.setAttribute('viewBox', (-pad) + ' ' + (-pad) + ' ' + (R.w + pad * 2) + ' ' + (R.d + pad * 2));
      svg.textContent = '';
      el('rect', { class: 'hf-floor', x: 0, y: 0, width: R.w, height: R.d }, svg);
      for (var x = 10; x < R.w; x += 10) el('line', { class: x % 100 === 0 ? 'hf-g2' : 'hf-g1', x1: x, y1: 0, x2: x, y2: R.d }, svg);
      for (var y = 10; y < R.d; y += 10) el('line', { class: y % 100 === 0 ? 'hf-g2' : 'hf-g1', x1: 0, y1: y, x2: R.w, y2: y }, svg);
      for (x = 100; x < R.w; x += 100) el('text', { class: 'hf-dim', x: x, y: -8, 'font-size': fs * .75, 'text-anchor': 'middle' }, svg, (x / 100) + 'm');
      for (y = 100; y < R.d; y += 100) el('text', { class: 'hf-dim', x: -8, y: y + fs * .25, 'font-size': fs * .75, 'text-anchor': 'end' }, svg, (y / 100) + 'm');
      el('text', { class: 'hf-dim', x: R.w / 2, y: -pad + fs, 'font-size': fs, 'text-anchor': 'middle' }, svg, m(R.w));
      var rx = R.w + pad - fs * .6;
      el('text', { class: 'hf-dim', x: rx, y: R.d / 2, 'font-size': fs, 'text-anchor': 'middle', transform: 'rotate(90 ' + rx + ' ' + R.d / 2 + ')' }, svg, m(R.d));
      el('rect', { class: 'hf-wall', x: 0, y: 0, width: R.w, height: R.d }, svg);

      var D = S.door;
      if (D.wall !== 'none') {
        var a = D.at, w = D.width, gl, hg, p1, p2, sw;
        if (D.wall === 'top') { gl = [a, 0, a + w, 0]; hg = [a, 0]; p1 = [a, w]; p2 = [a + w, 0]; sw = 0; }
        else if (D.wall === 'bottom') { gl = [a, R.d, a + w, R.d]; hg = [a, R.d]; p1 = [a, R.d - w]; p2 = [a + w, R.d]; sw = 1; }
        else if (D.wall === 'left') { gl = [0, a, 0, a + w]; hg = [0, a]; p1 = [w, a]; p2 = [0, a + w]; sw = 1; }
        else { gl = [R.w, a, R.w, a + w]; hg = [R.w, a]; p1 = [R.w - w, a]; p2 = [R.w, a + w]; sw = 0; }
        el('line', { class: 'hf-doorgap', x1: gl[0], y1: gl[1], x2: gl[2], y2: gl[3] }, svg);
        el('line', { class: 'hf-doorleaf', x1: hg[0], y1: hg[1], x2: p1[0], y2: p1[1] }, svg);
        el('path', { class: 'hf-doorarc', d: 'M' + p1[0] + ',' + p1[1] + ' A' + w + ',' + w + ' 0 0 ' + sw + ' ' + p2[0] + ',' + p2[1] }, svg);
      }
      results.forEach(r => r.zones.forEach(z => { var c = clip(z.z, R); if (c) el('rect', { class: 'hf-zone hf-zone--' + z.s, x: c.x, y: c.y, width: c.w, height: c.h }, svg); }));

      results.slice().sort((a, b) => (b.it.low ? 1 : 0) - (a.it.low ? 1 : 0)).forEach(r => {
        var it = r.it, f = r.f;
        var g = el('g', {
          class: 'hf-piece' + (it.low ? ' is-low' : '') + (it.kind === 'current' ? ' is-current' : '') + (it.id === S.sel ? ' is-sel' : '') + (r.status === 'bad' ? ' is-bad' : ''),
          tabindex: 0, role: 'button', 'data-id': it.id, 'aria-label': it.name + ', ' + m(it.w) + ' by ' + m(it.d) + '. ' + r.summary
        }, svg);
        // line drawing (sofa modules, chaises, corners, beds…) when the name tells us the shape
        var drawn = !!(window.HomeFitShapes && window.HomeFitShapes.draw(g, it, f, { handing: this.cfg.handing }));
        if (drawn) g.classList.add('is-drawn');
        else if (it.round) el('ellipse', { class: 'hf-body', cx: f.x + f.w / 2, cy: f.y + f.h / 2, rx: f.w / 2, ry: f.h / 2 }, g);
        else el('rect', { class: 'hf-body', x: f.x, y: f.y, width: f.w, height: f.h, rx: 3 }, g);
        if (!it.round && !drawn) {
          var v = dirOf('front', it.rot);
          var fl = v[1] === 1 ? [f.x + 4, f.y + f.h, f.x + f.w - 4, f.y + f.h] : v[1] === -1 ? [f.x + 4, f.y, f.x + f.w - 4, f.y] : v[0] === 1 ? [f.x + f.w, f.y + 4, f.x + f.w, f.y + f.h - 4] : [f.x, f.y + 4, f.x, f.y + f.h - 4];
          el('line', { class: 'hf-front', x1: fl[0], y1: fl[1], x2: fl[2], y2: fl[3] }, g);
        }
        var label = short(it.name, 22), tf = Math.min(fs, f.w / (label.length * 0.56), f.h / 2.4);
        if (tf >= 6) {
          el('text', { x: f.x + f.w / 2, y: f.y + f.h / 2 - tf * 0.15, 'font-size': tf, 'text-anchor': 'middle' }, g, label);
          el('text', { class: 'hf-small', x: f.x + f.w / 2, y: f.y + f.h / 2 + tf * 1.05, 'font-size': tf * 0.82, 'text-anchor': 'middle' }, g, it.w + ' × ' + it.d);
        }
      });
      var s = results.find(r => r.it.id === S.sel);
      if (s) s.zones.forEach(z => {
        var c = clip(z.z, R) || z.z;
        el('text', { class: 'hf-gaplbl hf-gaplbl--' + z.s, x: c.x + c.w / 2, y: c.y + c.h / 2 + fs * .35, 'font-size': fs * .95, 'text-anchor': 'middle' }, svg, m(z.g));
      });
    }
    renderSide(results) {
      var S = this.state, R = S.room, v = this.$('verdict');
      var worst = results.reduce((a, r) => Math.max(a, RANK[r.status]), 0);
      var nWarn = results.filter(r => r.status === 'warn').length, nBad = results.filter(r => r.status === 'bad').length;
      var fronts = [];
      results.forEach(r => { if (!r.it.low) r.zones.forEach(z => { if (z.side === 'front') fronts.push(z.g); }); });
      var tight = fronts.length ? Math.min.apply(null, fronts) : null;
      if (this.variantMissing && !results.length) { v.className = 'hf-verdict'; v.innerHTML = '<strong>Size not available for this option</strong><p>Choose another option, or contact us for exact dimensions.</p>'; }
      else if (!results.length) { v.className = 'hf-verdict'; v.innerHTML = '<strong>Your room is empty</strong><p>Search for furniture below and add it to your room.</p>'; }
      else if (worst === 0) { v.className = 'hf-verdict hf-verdict--ok'; v.innerHTML = '<span class="hf-ic" aria-hidden="true">✓</span><strong>Comfortable</strong><p>' + (tight != null ? m(tight) + ' minimum walkway in front of your pieces.' : 'Everything has the space it needs.') + '</p>'; }
      else if (worst === 1) { v.className = 'hf-verdict hf-verdict--warn'; v.innerHTML = '<span class="hf-ic" aria-hidden="true">!</span><strong>Tight squeeze</strong><p>' + nWarn + ' piece' + (nWarn > 1 ? 's are' : ' is') + ' short of ideal space. It fits, but moving around will feel snug.</p>'; }
      else { v.className = 'hf-verdict hf-verdict--bad'; v.innerHTML = '<span class="hf-ic" aria-hidden="true">✕</span><strong>Needs a rethink</strong><p>' + nBad + ' piece' + (nBad > 1 ? 's clash' : ' clashes') + ' with a wall, door or another piece, or has too little space to use.</p>'; }

      var area = R.w * R.d / 1e4, used = S.items.reduce((a, it) => a + (it.round ? Math.PI / 4 : 1) * it.w * it.d, 0) / 1e4, pct = Math.round(used / area * 100);
      this.$('stats').innerHTML = [
        ['Room size', m(R.w) + ' × ' + m(R.d) + ' · ' + area.toFixed(1) + ' m²'],
        ['Floor taken by furniture', used.toFixed(1) + ' m² (' + pct + '%)'],
        ['Open floor', (area - used).toFixed(1) + ' m²'],
        ['Tightest walkway', tight != null ? m(tight) : '—']
      ].map(r => '<div><dt>' + r[0] + '</dt><dd>' + r[1] + '</dd></div>').join('') +
        (pct > 45 ? '<div class="hf-note"><dt>Over 45% of the floor is furniture. Rooms usually start to feel crowded past this point.</dt></div>' : '');

      var label = { ok: 'Fits', warn: 'Tight', bad: 'Clash' };
      this.$('pieces').innerHTML = results.length ? results.map(r => {
        var it = r.it, tag = it.kind === 'current' ? ' <em>This product</em>' : it.kind === 'custom' ? ' <em>Your piece</em>' : '';
        var name = it.url && it.kind === 'product' ? '<a href="' + esc(it.url) + '">' + esc(it.name) + '</a>' : esc(it.name);
        return '<li data-id="' + it.id + '" class="' + (it.id === S.sel ? 'is-sel' : '') + '"><span class="hf-nm">' + name + tag + '</span>' +
          '<span class="hf-pill hf-pill--' + r.status + '">' + label[r.status] + '</span><span class="hf-msg">' + esc(r.summary) + '</span></li>';
      }).join('') : '<li class="hf-empty">No pieces yet.</li>';
    }
    renderToolbar() {
      var S = this.state, it = S.items.find(i => i.id === S.sel), tb = this.$('toolbar');
      var others = S.items.some(i => i.kind !== 'current');
      var extra = others ? '<button type="button" class="hf-btn" data-act="clear">' + (this.isProduct ? 'Clear other pieces' : 'Clear room') + '</button>' : '';
      if (!it) { tb.innerHTML = '<span class="hf-who hf-who--muted">' + (S.items.length ? 'Select a piece to rotate or move it.' : 'Add furniture to start planning.') + '</span>' + extra; return; }
      tb.innerHTML = '<span class="hf-who">' + esc(short(it.name, 40)) + '</span>' +
        '<button type="button" class="hf-btn" data-act="rot">Rotate 90°</button>' +
        '<button type="button" class="hf-btn" data-act="wall">Back to wall</button>' +
        '<button type="button" class="hf-btn" data-act="centre">Centre</button>' +
        (it.kind !== 'current' ? '<button type="button" class="hf-btn" data-act="del">Remove</button>' : '');
    }
  }

  customElements.define('home-fit', HomeFit);
  window.HomeFitParseDims = parseDims; // exposed for QA in the browser console
})();